import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Disclosure, Transition } from '@headlessui/react'
import FooterPagination from '../../../../pagination/FooterPagination'
import { useNotification } from '../../../../../providers/NotificationProvider'
import userService from '../../../../../services/user.service'
import { PaginatedResponse } from '../../../../../types/APIResponse'
import { MePayout } from '../../../../../types/Payout/MePayout'
import DateRangeSelect from '../../../../shared/DateRangeSelect'
import { HeroIcons } from '../../../../../lib/utils/heroIcons'
import PayoutsReviewTabElem from './PayoutReviewTabElem'
import { Daterange } from '../../../../../types/Daterange'
import { formatDateString } from '../../../../../lib/utils/formatDate'
import PayoutsReviewTabDetails from './Details/PayoutReviewTabDetails'
import useLocale from '../../../../../hooks/useLocale'
import { classNames } from '../../../../../lib/utils/classNames'

const PAGE_SIZE = 6

export default function PayoutsReviewTab() {
  const { t } = useTranslation('dashboard')
  const { locale } = useLocale()

  const { error: displayError } = useNotification()

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [maxPage, setMaxPage] = useState<number>(0)
  const [totalElements, setTotalElements] = useState<number>(0)

  const [selectedDateRange, setSelectedDateRange] = useState<Daterange | null>()

  const [fetchMePayoutsIsLoading, setFetchMePayoutsIsLoading] =
    useState<boolean>(false)
  const [fetchMePayoutIsLoading, setFetchMePayoutIsLoading] =
    useState<boolean>(false)

  const [mePayouts, setMePayouts] = useState<MePayout[]>([])
  const [currentPayout, setCurrentPayout] = useState<MePayout | undefined>(
    undefined,
  )

  const fetchMePayoutByTransactionId = async (
    transactionId: number,
  ): Promise<void> => {
    setFetchMePayoutIsLoading(true)
    try {
      const mePayout: MePayout =
        await userService.getMePayoutByTransactionId(transactionId)
      setCurrentPayout(mePayout)
    } catch (err) {
      displayError(
        t('payoutsReview.tab.errors.fetchPayout.title'),
        t('payoutsReview.tab.errors.fetchPayout.text'),
        10000,
      )
    } finally {
      setFetchMePayoutIsLoading(false)
    }
  }

  const fetchMePayouts = async (): Promise<void> => {
    setFetchMePayoutsIsLoading(true)
    try {
      const paginatedResponse: PaginatedResponse<MePayout[]> =
        await userService.getMePayouts({
          page: currentPage,
          pageSize: PAGE_SIZE,
          daterange: selectedDateRange,
        })
      setMePayouts(paginatedResponse.data)
      setMaxPage(paginatedResponse.pagination.maxPages)
      setTotalElements(paginatedResponse.pagination.totalElements)
    } catch (err) {
      displayError(
        t('payoutsReview.tab.errors.fetchPayouts.title'),
        t('payoutsReview.tab.errors.fetchPayouts.text'),
        10000,
      )
    } finally {
      setFetchMePayoutsIsLoading(false)
    }
  }

  useEffect(() => {
    fetchMePayouts()
  }, [currentPage, selectedDateRange])

  return (
    <div className="relative flex grow flex-col rounded-md bg-white">
      <Disclosure>
        {({ open, close }) => (
          <div className="flex grow">
            <div
              className={classNames(
                'flex grow flex-col',
                open ? 'blur-sm md:blur-0' : '',
              )}
            >
              <div className="flex w-full items-center border-b border-gray-200 bg-white p-3">
                <div className="flex grow items-center space-x-1">
                  <HeroIcons.CurrencyDollarIcon className="hidden h-auto w-6 text-gray-800 sm:block" />
                  <p className="text-md whitespace-nowrap font-medium text-gray-800 sm:text-xl">
                    {t('payoutsReview.title')}
                  </p>
                </div>
                <div>
                  <DateRangeSelect
                    onDateRangeChange={setSelectedDateRange}
                    defaultDateRange={null}
                  />
                </div>
              </div>

              <div className="flex grow flex-col">
                {mePayouts.length > 0 ? (
                  mePayouts.map((mePayout, index) => (
                    <Disclosure.Button
                      key={index}
                      className={classNames(
                        'border-b border-gray-100 p-3 text-left hover:bg-sheltersBlue/10',
                        currentPayout?.id === mePayout.id
                          ? 'bg-gray-100/30'
                          : '',
                      )}
                      onClick={() => {
                        close()
                        fetchMePayoutByTransactionId(mePayout.id)
                      }}
                    >
                      <PayoutsReviewTabElem mePayout={mePayout} />
                    </Disclosure.Button>
                  ))
                ) : (
                  <p className="py-4 text-center text-gray-500">
                    {t('payoutsReview.tab.rows.noPayoutsFound')}
                  </p>
                )}
              </div>

              <div className="w-full bg-white">
                <FooterPagination
                  currentPage={currentPage}
                  maxPages={maxPage}
                  maxResults={totalElements}
                  onPageChange={(page) => {
                    setCurrentPage(page)
                  }}
                  isLoading={fetchMePayoutsIsLoading}
                />
              </div>
            </div>

            <Transition
              show={open}
              enter="transition-opacity duration-300 ease-in-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-300 ease-in-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              as="div"
              className="absolute right-0 z-10 h-full w-[90%] overflow-y-auto border-l border-gray-200 bg-white p-3 md:relative md:h-auto md:w-[40%]"
            >
              <Disclosure.Panel>
                <div className="flex h-full flex-col overflow-hidden">
                  {fetchMePayoutIsLoading ? (
                    <div></div>
                  ) : currentPayout ? (
                    <>
                      <div className="flex w-full items-center">
                        <div className="grow">
                          <div className="flex space-x-1">
                            <p className="text-gray-800">
                              {t('payoutsReview.tabDetails.title')}
                            </p>
                            <p className="font-medium text-gray-800">
                              #{currentPayout.id}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-thin text-gray-700">
                              {formatDateString(
                                currentPayout.updatedAt.toString(),
                                locale,
                              )}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            close()
                          }}
                          className="text-gray-700 hover:text-gray-500"
                        >
                          <HeroIcons.XMarkIcon className="h-8 w-auto" />
                        </button>
                      </div>

                      <div className="grow">
                        <PayoutsReviewTabDetails mePayout={currentPayout} />
                      </div>
                    </>
                  ) : (
                    <div className="flex h-full flex-col overflow-hidden">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            close()
                          }}
                          className="text-gray-700 hover:text-gray-500"
                        >
                          <HeroIcons.XMarkIcon className="h-8 w-auto" />
                        </button>
                      </div>
                      <div className="flex grow items-center justify-center text-center text-gray-500">
                        {t('payoutsReview.tabDetails.errors.nothingFound')}
                      </div>
                    </div>
                  )}
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    </div>
  )
}
